import { RawDraftEntityRange } from 'draft-js';
import {
  HashtagMentionTypeEnum,
  ICreateActivityFeedFileCabinetHashtag,
  ICreateActivityFeedHashtag,
  ICreateActivityFeedPhaseItemHashtag,
  ICreateActivityFeedSelectionHashtag,
  ICreateActivityFeedSelectionOptionHashtag,
  IGetActivityFeedFileCabinetHashtag,
  IGetActivityFeedHashtag,
  IGetActivityFeedPhaseItemHashtag,
  IGetActivityFeedSelectionHashtag,
  IGetActivityFeedSelectionOptionHashtag,
  IHashtagMention,
} from 'redux-store/api/activity-feed/hashtags.types';

export const mapHashtagFirebase = ({
  entityRange,
  entityMention,
  block,
}: {
  entityRange: RawDraftEntityRange;
  entityMention: IHashtagMention;
  block: number;
}): IGetActivityFeedHashtag => {
  const { offset, length, key } = entityRange;
  const { type, id, name, mimeType } = entityMention;

  const createFileCabinetPayload = (): IGetActivityFeedFileCabinetHashtag => ({
    offset,
    length,
    key,
    type: HashtagMentionTypeEnum.fileCabinetItem,
    block,
    fileCabinet: {
      id,
      name,
      mimeType,
    },
  });

  const createPhaseItemPayload = (): IGetActivityFeedPhaseItemHashtag => ({
    offset,
    length,
    key,
    type: HashtagMentionTypeEnum.phaseItem,
    block,
    phaseItem: {
      id,
      title: name,
    },
  });

  const createSelectionPayload = (): IGetActivityFeedSelectionHashtag => ({
    offset,
    length,
    key,
    type: HashtagMentionTypeEnum.selection,
    block,
    selection: {
      id,
      title: name,
    },
  });

  const createSelectionOptionPayload = (): IGetActivityFeedSelectionOptionHashtag => ({
    offset,
    length,
    key,
    type: HashtagMentionTypeEnum.selectionOption,
    block,
    selectionOption: {
      id,
      selectionItemId: entityMention.selectionItemId ?? null,
      title: name,
    },
  });

  switch (type) {
    case HashtagMentionTypeEnum.fileCabinetItem:
      return createFileCabinetPayload();
    case HashtagMentionTypeEnum.phaseItem:
      return createPhaseItemPayload();
    case HashtagMentionTypeEnum.selection:
      return createSelectionPayload();
    case HashtagMentionTypeEnum.selectionOption:
      return createSelectionOptionPayload();
    default:
      throw new Error(`Unknown hashtag mention type: ${type}`);
  }
};

export const mapHashtagMentionPayload = ({
  entityRange,
  entityMention,
  block,
}: {
  entityRange: RawDraftEntityRange;
  entityMention: IHashtagMention;
  block: number;
}): ICreateActivityFeedHashtag => {
  const { offset, length, key } = entityRange;
  const { type, id } = entityMention;

  const createFileCabinetPayload = (): ICreateActivityFeedFileCabinetHashtag => ({
    offset,
    length,
    key,
    type: HashtagMentionTypeEnum.fileCabinetItem,
    block,
    fileCabinetId: id,
  });

  const createPhaseItemPayload = (): ICreateActivityFeedPhaseItemHashtag => ({
    offset,
    length,
    key,
    type: HashtagMentionTypeEnum.phaseItem,
    block,
    phaseItemId: id,
  });

  const createSelectionPayload = (): ICreateActivityFeedSelectionHashtag => ({
    offset,
    length,
    key,
    type: HashtagMentionTypeEnum.selection,
    block,
    selectionId: id,
  });

  const createSelectionOptionPayload = (): ICreateActivityFeedSelectionOptionHashtag => ({
    offset,
    length,
    key,
    type: HashtagMentionTypeEnum.selectionOption,
    block,
    selectionOptionId: id,
  });

  switch (type) {
    case HashtagMentionTypeEnum.fileCabinetItem:
      return createFileCabinetPayload();
    case HashtagMentionTypeEnum.phaseItem:
      return createPhaseItemPayload();
    case HashtagMentionTypeEnum.selection:
      return createSelectionPayload();
    case HashtagMentionTypeEnum.selectionOption:
      return createSelectionOptionPayload();
    default:
      throw new Error(`Unknown hashtag mention type: ${type}`);
  }
};

export const mapHashtag = (item: IGetActivityFeedHashtag): IHashtagMention => {
  const handleFileCabinetItem = (item: IGetActivityFeedFileCabinetHashtag) => ({
    name: item?.fileCabinet?.name,
    id: item?.fileCabinet?.id,
    type: item?.type,
    mimeType: item?.fileCabinet?.mimeType,
  });

  const handlePhaseItem = (item: IGetActivityFeedPhaseItemHashtag) => ({
    name: item?.phaseItem?.title,
    id: item?.phaseItem?.id,
    type: item?.type,
  });

  const handleSelection = (item: IGetActivityFeedSelectionHashtag) => ({
    name: item?.selection?.title,
    id: item?.selection?.id,
    type: item?.type,
  });

  const handleSelectionOption = (item: IGetActivityFeedSelectionOptionHashtag) => ({
    name: item?.selectionOption?.title,
    id: item?.selectionOption?.id,
    selectionItemId: item?.selectionOption?.selectionItemId,
    type: item?.type,
  });

  const handlers: Record<
    HashtagMentionTypeEnum,
    (item: IGetActivityFeedHashtag) => IHashtagMention
  > = {
    [HashtagMentionTypeEnum.fileCabinetItem]: handleFileCabinetItem,
    [HashtagMentionTypeEnum.phaseItem]: handlePhaseItem,
    [HashtagMentionTypeEnum.selection]: handleSelection,
    [HashtagMentionTypeEnum.selectionOption]: handleSelectionOption,
  };

  const handler = handlers[item.type];

  return handler(item);
};

export const createHashtagPayload = ({
  type,
  id,
  mimeType,
  projectId,
}: {
  type: HashtagMentionTypeEnum;
  id: number;
  mimeType?: string;
  projectId: number;
}) => {
  switch (type) {
    case HashtagMentionTypeEnum.fileCabinetItem:
      return { hashtagType: type, fileItemId: id, mimeType, projectId };
    case HashtagMentionTypeEnum.phaseItem:
      return { hashtagType: type, phaseItemId: id, projectId };
    case HashtagMentionTypeEnum.selectionOption:
    case HashtagMentionTypeEnum.selection:
      return { hashtagType: type, selectionItemId: id, projectId };
    default:
      throw new Error(`Unsupported hashtag type: ${type}`);
  }
};
